// Master plan (APS P2, docs/design/aps.md §6) — data access.
//
//   plan        rpc master_plan(company, from, to) — kits with their orders (coverage and
//               bottleneck from the ATP snapshot) and loose open orders; one round-trip.
//   load        rpc work_center_load(company, from, to) — capacity and load per day.
//   components  rpc master_plan_components(company, from, to) — short components of the
//               window's kits, per day; fetched only while the component strip is shown.
//   moves       shift_production_kit / move_production_order (features/production/kits.ts).

import type { MasterPlan, PlanComponents, WorkCenterLoad } from "@patronix/domain";
import { queryOptions } from "@tanstack/react-query";
import { isDemoMode, supabase } from "@/lib/supabase";

export interface PlanWindow {
  companyId: string;
  from: string;
  to: string;
}

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

const args = (w: PlanWindow) => ({ p_company_id: w.companyId, p_from: w.from, p_to: w.to });

async function getPlan(w: PlanWindow, signal: AbortSignal): Promise<MasterPlan> {
  if (isDemoMode) return { from: w.from, to: w.to, today: w.from, kits: [], kits_has_more: false, loose: [], loose_has_more: false };
  const { data, error } = await db().rpc("master_plan", args(w)).abortSignal(signal);
  if (error) throw error;
  return data as MasterPlan;
}

async function getLoad(w: PlanWindow, signal: AbortSignal): Promise<WorkCenterLoad> {
  if (isDemoMode) return { from: w.from, to: w.to, today: w.from, work_centers: [] };
  const { data, error } = await db().rpc("work_center_load", args(w)).abortSignal(signal);
  if (error) throw error;
  return data as WorkCenterLoad;
}

async function getComponents(w: PlanWindow, signal: AbortSignal): Promise<PlanComponents> {
  if (isDemoMode) return { from: w.from, to: w.to, today: w.from, components: [] };
  const { data, error } = await db().rpc("master_plan_components", args(w)).abortSignal(signal);
  if (error) throw error;
  return data as PlanComponents;
}

export const masterPlanKeys = {
  all: ["planning", "master-plan"] as const,
  plan: (w: PlanWindow) => [...masterPlanKeys.all, "plan", w] as const,
  load: (w: PlanWindow) => [...masterPlanKeys.all, "load", w] as const,
  components: (w: PlanWindow) => [...masterPlanKeys.all, "components", w] as const,
};

export function masterPlanQuery(w: PlanWindow) {
  return queryOptions({
    queryKey: masterPlanKeys.plan(w),
    queryFn: ({ signal }) => getPlan(w, signal),
    staleTime: 30_000,
    enabled: !!w.companyId,
    placeholderData: (prev) => prev,
  });
}

export function workCenterLoadQuery(w: PlanWindow) {
  return queryOptions({
    queryKey: masterPlanKeys.load(w),
    queryFn: ({ signal }) => getLoad(w, signal),
    staleTime: 30_000,
    enabled: !!w.companyId,
    placeholderData: (prev) => prev,
  });
}

export function planComponentsQuery(w: PlanWindow, enabled: boolean) {
  return queryOptions({
    queryKey: masterPlanKeys.components(w),
    queryFn: ({ signal }) => getComponents(w, signal),
    staleTime: 30_000,
    enabled: enabled && !!w.companyId,
    placeholderData: (prev) => prev,
  });
}

/** Outcome of load_sample_master_plan (planning examples on top of the sample data). */
export type PlanningExamplesResult =
  | { ok: true; kits: number; orders: number }
  | { ok: false; reason: "noSample" | "otherCompany" | "changed" | "already" | "denied" | "failed" };

/** Kits and orders over the next weeks in a company with the DEMO- data (one RPC, one transaction). */
export async function loadPlanningExamples(companyId: string): Promise<PlanningExamplesResult> {
  if (isDemoMode || !supabase) return { ok: false, reason: "failed" };
  const { data, error } = await supabase.rpc("load_sample_master_plan", { p_company_id: companyId });
  if (error) {
    const reason =
      error.code === "P0002"
        ? /another company/.test(error.message)
          ? "otherCompany"
          : "noSample"
        : error.code === "23505"
          ? "already"
          : error.code === "42501"
            ? "denied"
            : /sample data was changed/.test(error.message)
              ? "changed"
              : "failed";
    return { ok: false, reason };
  }
  const r = data as { kits: number; orders: number };
  return { ok: true, kits: r.kits, orders: r.orders };
}
